import type { ReactNode } from "react";

import { Box, Column, Row, Text, TextWithTooltip } from "@hightouch/htui";

import { CommentAuthorAvatar } from "./comment-author-avatar";
import type { CommentAuthor } from "./comment-item";
import { formatCommentAge } from "./utils";

type Props = {
  author: CommentAuthor;
  createdAt: string;
  /** Video offset as `M:SS`, on threads anchored to a moment. */
  timestampLabel?: string;
  /** Shown on a second line, under the name and age. */
  subtitle?: string | null;
  /** Between the name and the age, such as the review the comment came with. */
  badge?: ReactNode;
};

/**
 * Who wrote a comment and how long ago, on one line, with an optional subtitle
 * under it. Used by the rail and the inline card; the notification feed stacks
 * the same fields against a taller avatar, so it composes them itself.
 *
 * The name truncates and the timestamp holds its width — an email-length name
 * would otherwise squeeze the time until it wrapped the row onto two lines.
 * With a badge, the name holds instead and the badge and age wrap under it, so
 * a narrow reply still says who wrote it.
 *
 * The trailing text sits in a `Row` rather than a `Box`, matching the
 * notification feed. The name goes through `TextWithTooltip`, whose inner text
 * is a block for the ellipsis; an inline sibling reserves room under the
 * baseline for descenders and so centers a shade lower. A flex parent takes its
 * height from the text rather than the page's leading, which lines the two up
 * without stretching the row.
 */
export const CommentAuthorLine = ({
  author,
  createdAt,
  timestampLabel,
  subtitle,
  badge,
}: Props) => (
  <Row align="center" gap={2} minWidth={0}>
    <CommentAuthorAvatar author={author} size="sm" />

    <Column minWidth={0}>
      <Row
        align="center"
        columnGap={2}
        rowGap={1}
        minWidth={0}
        flexWrap={badge ? "wrap" : undefined}
      >
        <Box minWidth={0} {...(badge && { flexShrink: 0, maxWidth: "100%" })}>
          <TextWithTooltip size="sm" fontWeight="medium" color="text.primary">
            {author.name}
          </TextWithTooltip>
        </Box>

        <Row align="center" gap={2} flexShrink={0}>
          {badge && <Row flexShrink={0}>{badge}</Row>}

          {timestampLabel && (
            <Row flexShrink={0}>
              <Text
                size="sm"
                fontWeight="medium"
                color="link.default"
                whiteSpace="nowrap"
              >
                {timestampLabel}
              </Text>
            </Row>
          )}

          <Row flexShrink={0}>
            <Text size="sm" color="text.tertiary" whiteSpace="nowrap">
              {formatCommentAge(createdAt)}
            </Text>
          </Row>
        </Row>
      </Row>

      {subtitle && (
        <TextWithTooltip size="sm" color="text.tertiary">
          {subtitle}
        </TextWithTooltip>
      )}
    </Column>
  </Row>
);
